// The ONE module in dash-user that imports @bookingdei/api-client (CB-2,
// rule 7.1-3). Every screen calls `api` from here — never a raw request, never a
// second client. Swapping the dev mock for the live api Worker is a config change
// (VITE_API_BASE_URL / VITE_USE_MOCK), not a code change.
import { createClient, ApiError } from '@bookingdei/api-client/client';
import type { Client } from '@bookingdei/api-client/client';

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://127.0.0.1:8787/v1';

const useMock =
  (import.meta.env.VITE_USE_MOCK ?? (import.meta.env.DEV ? 'true' : 'false')) === 'true';

// The mock transport and its seed data are dev/demo-only. A static import shipped
// all of it in the production bundle even with VITE_USE_MOCK=false. Loading it
// through a dynamic import that is reached only when `useMock` is true lets Vite
// drop it from real builds entirely (VITE_USE_MOCK is replaced at build time, so
// the mock branch is dead code there), while dev, tests and the design-parity
// harness (VITE_MOCK_FIXTURE=design) keep the same mock via a lazily-loaded chunk.
const mockFetch: typeof fetch = async (input, init) => {
  const { mockTransport } = await import('./mock/mock-transport');
  return mockTransport(input, init);
};

// Access token held in memory (AP-D4) — never localStorage. There is no login form
// on this surface: sign-in happens once on the website, which sets a shared HttpOnly
// refresh cookie. On load the api-client's first /me 401s, the client calls
// /auth/refresh (cookie sent via `credentials: 'include'`), and retries with the
// fresh access token — so the customer arrives already signed in.
let accessToken: string | null = null;
export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function hasAccessToken(): boolean {
  return accessToken !== null;
}

// A bare client used ONLY for POST /auth/refresh — no getAccessToken and no refresher
// of its own, so a failed refresh can never recurse into another refresh.
const refreshClient: Client = createClient({ baseUrl, defaultLang: 'en', credentials: 'include' });

async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = (await refreshClient.POST('/auth/refresh', { body: { client: 'web' } })) as {
      access_token?: string;
    };
    setAccessToken(res.access_token ?? null);
    return res.access_token ?? null;
  } catch {
    setAccessToken(null);
    return null;
  }
}

// Boot without the guaranteed 401 (perf): a fresh page load never holds an access
// token, so the first request (GET /me) used to 401, refresh, then retry — three
// serial round trips before the shell could render. Instead the very first request
// refreshes up front (cookie-backed) and every request of that first wave awaits the
// same promise. Only once per page load; afterwards the 401 → refresh → retry path
// (single-flight, api-client) takes over unchanged.
let bootRefresh: Promise<unknown> | null = null;
async function bootAccessToken(): Promise<string | null> {
  if (accessToken === null && bootRefresh === null) bootRefresh = refreshAccessToken();
  if (bootRefresh) await bootRefresh;
  return accessToken;
}

export const api: Client = createClient({
  baseUrl,
  defaultLang: 'en',
  ...(useMock
    ? { getAccessToken: () => accessToken, fetch: mockFetch }
    : { getAccessToken: bootAccessToken, credentials: 'include' as const, refreshAccessToken }),
});

// End the shared session: clear the refresh cookie server-side, drop the in-memory
// token, then let the gate bounce to the central login on the next render.
export async function signOut(): Promise<void> {
  try {
    await refreshClient.POST('/auth/logout');
  } catch {
    /* best-effort — the cookie is cleared server-side; the token is dropped either way */
  }
  setAccessToken(null);
}

export { ApiError };
